import { Injectable } from "@angular/core";
import { saveAs } from "file-saver";
import { Base64 } from "js-base64";
import { HttpService } from "./http.service";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export type MediaKind = "image" | "video" | "audio";

@Injectable({
  providedIn: "root",
})
export class HelperService {
  allowedImg = [".png", ".jpeg", ".jpg", ".gif"];
  allowedAudio = [
    ".heic",
    ".mp3",
    ".m4a",
    ".mp4",
    ".webm",
    ".mpeg",
    ".aac",
    ".wav",
  ];
  allowedVideo = [".mp4", ".ogv", ".webm"];
  private readonly IMAGE_EXT = new Set([
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "bmp",
    "tiff",
    "tif",
    "svg",
    "avif",
  ]);
  private readonly VIDEO_EXT = new Set([
    "mp4",
    "webm",
    "ogg",
    "ogv",
    "mov",
    "m4v",
    "avi",
    "mkv",
    "flv",
    "wmv",
  ]);
  private readonly AUDIO_EXT = new Set([
    "mp3",
    "wav",
    "ogg",
    "oga",
    "m4a",
    "aac",
    "flac",
    "alac",
    "weba",
    "opus",
  ]);

  constructor(protected httpService: HttpService) {}

  static uuidv4(): string {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  }

  /**
   * Определяет режим работы с данными на основе URL.
   *
   * Режимы:
   * - **create** — создаётся новый элемент или экземпляр сущности.
   * - **edit** — редактируется (обновляется) ранее созданный элемент.
   * - **copy** — создаётся копия ранее созданного элемента или экземпляра сущности.
   */
  static detectModificationModeByUrlPath(
    url: string
  ): "copy" | "edit" | "create" {
    if (url.includes("/copy")) return "copy";
    if (url.includes("/edit")) return "edit";
    return "create";
  }

  /**
   * Конвертация в транслит
   * @param str
   */
  static convertToTranslit(str: string): string {
    const arrRu = [
      " ",
      "Я",
      "я",
      "Ю",
      "ю",
      "Ч",
      "ч",
      "Ш",
      "ш",
      "Щ",
      "щ",
      "Ж",
      "ж",
      "А",
      "а",
      "Б",
      "б",
      "В",
      "в",
      "Г",
      "г",
      "Д",
      "д",
      "Е",
      "е",
      "Ё",
      "ё",
      "З",
      "з",
      "И",
      "и",
      "Й",
      "й",
      "К",
      "к",
      "Л",
      "л",
      "М",
      "м",
      "Н",
      "н",
      "О",
      "о",
      "П",
      "п",
      "Р",
      "р",
      "С",
      "с",
      "Т",
      "т",
      "У",
      "у",
      "Ф",
      "ф",
      "Х",
      "х",
      "Ц",
      "ц",
      "Ы",
      "ы",
      "Ь",
      "ь",
      "Ъ",
      "ъ",
      "Э",
      "э",
    ];
    const arrEn = [
      "-",
      "Ya",
      "ya",
      "Yu",
      "yu",
      "Ch",
      "ch",
      "Sh",
      "sh",
      "Sh",
      "sh",
      "Zh",
      "zh",
      "A",
      "a",
      "B",
      "b",
      "V",
      "v",
      "G",
      "g",
      "D",
      "d",
      "E",
      "e",
      "E",
      "e",
      "Z",
      "z",
      "I",
      "i",
      "J",
      "j",
      "K",
      "k",
      "L",
      "l",
      "M",
      "m",
      "N",
      "n",
      "O",
      "o",
      "P",
      "p",
      "R",
      "r",
      "S",
      "s",
      "T",
      "t",
      "U",
      "u",
      "F",
      "f",
      "H",
      "h",
      "Ts",
      "ts",
      "Y",
      "y",
      "",
      "",
      "",
      "",
      "E",
      "e",
    ];

    for (let i = 0; i < arrRu.length; i++) {
      let reg = new RegExp(arrRu[i], "g");
      str = str.replace(reg, arrEn[i]);
    }
    return str;
  }

  /**
   * Генерация строки определенной длины
   * @param count
   */
  static createRandomString(count: number = 6): string {
    const chars: string =
      "0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz";
    let randomstring: string = "";
    for (let i = 0; i < count; i++) {
      let rnum = Math.floor(Math.random() * chars.length);
      randomstring += chars.substring(rnum, rnum + 1);
    }
    return randomstring;
  }

  /**
   * Создание нового файла с содержимым из response и именем файла из Content-Disposition
   * с последующим скачиванием его.
   * @param data
   * @param contentDisposition
   * @param base64Name
   */
  static downloadFile(
    data: any,
    contentDisposition: string,
    base64Name = true,
    type = "text/plain"
  ): void {
    const blob = new Blob([data], { type: type });
    // Формируем дефолтное имя файла
    let filename = `${(Math.random() * 1e8).toFixed()}_${
      new Date().getFullYear() +
      "-" +
      ("0" + (new Date().getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + new Date().getDate()).slice(-2)
    }`;

    if (contentDisposition && contentDisposition.length > 0) {
      let filenames = contentDisposition.match(
        /(filename\*=|filename=)\"?(.*)\"?/
      );
      if (filenames[2]) {
        filenames[2] = filenames[2].replace(/\"/, "");
        filename = decodeURI(filenames[2].replace(/.*''/, ""));
      }
    }

    filename = base64Name ? Base64.decode(filename) : filename;

    saveAs(blob, filename);
  }

  /**
   * Создание нового файла с содержимым из response и именем файла из Content-Disposition
   * с последующим скачиванием его.
   * @param data
   * @param contentDisposition
   * @param base64Name
   */
  static downloadFileSimple(
    data: any,
    contentDisposition: string,
    base64Name = true,
    type = "text/plain"
  ): void {
    const blob = new Blob([data], { type: type });
    // Формируем дефолтное имя файла
    let filename = ``;

    if (contentDisposition && contentDisposition.length > 0) {
      let filenames = contentDisposition.match(
        /(filename\*=|filename=)\"?(.*)\"?/
      );
      if (filenames[2]) {
        filenames[2] = filenames[2].replace(/\"/, "");
        filename = decodeURI(filenames[2].replace(/.*''/, ""));
      }
    }

    filename = base64Name ? Base64.decode(filename) : filename;

    saveAs(blob, filename);
  }

  /**
   * Создание нового файла с содержимым из response и именем файла из Content-Disposition
   * с последующим скачиванием его.
   * @param data
   * @param contentDisposition
   * @param base64Name
   */
  static downloadFileTest(
    data: any,
    contentDisposition: string,
    base64Name = true
  ): void {
    // console.log("downloadFileTest",contentDisposition)
    const blob = new Blob([String.fromCharCode(0xfeff), data], {
      type: "text/csv;charset=utf-8",
    });
    let filename = `${(Math.random() * 1e8).toFixed()}_${
      new Date().getFullYear() +
      "-" +
      ("0" + (new Date().getMonth() + 1)).slice(-2) +
      "-" +
      ("0" + new Date().getDate()).slice(-2)
    }`;

    if (contentDisposition && contentDisposition.length > 0) {
      let filenames = contentDisposition.match(
        /(filename\*=|filename=)\"?(.*)\"?/
      );

      if (filenames[2]) {
        filenames[2] = filenames[2].replace(/\"/, "");
        filename = decodeURI(filenames[2].replace(/.*''/, ""));
      }
    }
    filename = base64Name ? Base64.decode(filename) : filename;
    saveAs(blob, filename, { autoBom: true });
  }

  static addUTF8BOMToString(inputString) {
    // Create a Uint8Array containing the UTF-8 BOM (Byte Order Mark).
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

    // Convert the input string to a Uint8Array.
    const textBytes = new TextEncoder().encode(inputString);

    // Combine the BOM and the text bytes into a single Uint8Array.
    const resultBytes = new Uint8Array(bom.length + textBytes.length);
    resultBytes.set(bom, 0);
    resultBytes.set(textBytes, bom.length);

    // Convert the combined bytes back to a string.
    const resultString = new TextDecoder("utf-8").decode(resultBytes);

    return resultString;
  }

  static addUTF8BOMToStringOld(inputString) {
    // Create a Uint8Array containing the UTF-8 BOM (Byte Order Mark).
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

    // Convert the input string to a Uint8Array.
    const textBytes = new TextEncoder().encode(inputString);

    // Combine the BOM and the text bytes into a single Uint8Array.
    const resultBytes = new Uint8Array(bom.length + textBytes.length);
    resultBytes.set(bom, 0);
    resultBytes.set(textBytes, bom.length);

    // Convert the combined bytes back to a string.
    const resultString = new TextDecoder("utf-8").decode(resultBytes);

    return resultString;
  }

  static expandPolygon(coords: number[][]) {
    if (!coords || !coords.length) {
      return null;
    }
    let polygon = [];

    switch (coords.length) {
      case 1: {
        polygon = this.createPolygonByPoint(coords, 12, 150);
        break;
      }
      case 2: {
        polygon = this.createPolygonByTwoPoint(coords, 150);
        break;
      }
      default: {
        // polygon = this.createPolygonByMoreThreePoint(coords, 150);
        polygon = coords;
      }
    }

    return polygon;
  }

  /**
   * Кастомная валидация формы с контролами startDate и endDate
   * @control AbstractControl
   */
  public static dateRangeValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const startDate = control.get("startDate")?.value;
    const endDate = control.get("endDate")?.value;
    const MINIMUM_DIFF_DAY = 5;

    if (!startDate || !endDate) return null;

    const start = new Date(startDate).setHours(0, 0, 0, 0);
    const end = new Date(endDate).setHours(0, 0, 0, 0);

    const minEndDate = start + MINIMUM_DIFF_DAY * 24 * 60 * 60 * 1000;

    return end >= minEndDate ? null : { dateRangeInvalid: true };
  };

  public static formatAsUtcIgnoringOriginalTimezone(
    dateString: string
  ): string {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Месяцы 0-11
    const day = String(date.getDate()).padStart(2, "0");

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
  }

  /** Делает  все свыойства в объекте, с маленькой буквы (для параметров запроса Визитов) */
  static decapitalizeKeys(obj: any): any {
    if (!obj || typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      return obj.map(HelperService.decapitalizeKeys);
    }

    var result: any = {};
    Object.keys(obj).forEach(function (key) {
      var newKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[newKey] = HelperService.decapitalizeKeys(obj[key]);
    });

    return result;
  }

  private static createPolygonByPoint(
    coords: number[][],
    countAngles: number = 12,
    distance: number = 150
  ): number[][] {
    if (coords.length !== 1 || countAngles === 0) {
      return null;
    }

    const azimut = 360 / countAngles;
    const earth = (6371000 * Math.PI) / 180;
    let polygon = [];

    for (let i = 0; i < countAngles; i++) {
      let rad = (azimut * i * Math.PI) / 180;

      let x1 = coords[0][0] + (distance * Math.cos(rad)) / earth;
      let y1 =
        coords[0][1] +
        (distance * Math.sin(rad)) /
          Math.cos((coords[0][0] * Math.PI) / 180) /
          earth;

      polygon.push([x1, y1]);
    }

    return polygon;
  }

  private static getCenterPolygon(coords: number[][]): number[] {
    if (!coords || !coords.length) {
      return null;
    }

    return [
      coords.reduce((x, y) => x + y[0], 0) / coords.length,
      coords.reduce((x, y) => x + y[1], 0) / coords.length,
    ];
  }

  private static createPolygonByMoreThreePoint(
    coords: number[][],
    distance: number = 150
  ) {
    if (!coords || !coords.length) {
      return null;
    }

    const earth = (6371000 * Math.PI) / 180;
    const center = this.getCenterPolygon(coords);
    const polygon = [];

    for (let i = 0; i < coords.length; i++) {
      let rad = Math.atan2(coords[i][1] - center[1], coords[i][0] - center[0]);

      let x1 = coords[i][0] + (distance * Math.cos(rad)) / earth;
      let y1 =
        coords[i][1] +
        (distance * Math.sin(rad)) /
          Math.cos((coords[i][0] * Math.PI) / 180) /
          earth;

      polygon.push([x1, y1]);
    }

    return polygon;
  }

  private static createPolygonByTwoPoint(
    coords: number[][],
    distance: number = 150
  ) {
    if (!coords || !coords.length) {
      return null;
    }

    const earth = (6371000 * Math.PI) / 180;
    const center = this.getCenterPolygon(coords);
    const polygon = [];

    const rad45deg = (45 * Math.PI) / 180;

    for (let i = 0; i < coords.length; i++) {
      let rad1 =
        Math.atan2(coords[i][1] - center[1], coords[i][0] - center[0]) +
        rad45deg;

      let x1 = coords[i][0] + (distance * Math.cos(rad1)) / earth;
      let y1 =
        coords[i][1] +
        (distance * Math.sin(rad1)) /
          Math.cos((coords[i][0] * Math.PI) / 180) /
          earth;

      polygon.push([x1, y1]);

      let rad2 =
        Math.atan2(coords[i][1] - center[1], coords[i][0] - center[0]) -
        rad45deg;

      let x2 = coords[i][0] + (distance * Math.cos(rad2)) / earth;
      let y2 =
        coords[i][1] +
        (distance * Math.sin(rad2)) /
          Math.cos((coords[i][0] * Math.PI) / 180) /
          earth;

      polygon.push([x2, y2]);
    }

    return polygon;
  }

  /**
   * Возвращает тип медиафайла по расширению.
   * @param filenameOrExt — имя файла или расширение (с точкой/без).
   * @returns 'image' | 'video' | 'audio' | undefined (если не распознано)
   */
  public getMediaKindByExtension(filenameOrExt: string): MediaKind | undefined {
    if (!filenameOrExt) return undefined;

    const ext = this.extractExtension(filenameOrExt);
    if (!ext) return undefined;

    if (this.IMAGE_EXT.has(ext)) return "image";
    if (this.VIDEO_EXT.has(ext)) return "video";
    if (this.AUDIO_EXT.has(ext)) return "audio";

    return undefined;
  }

  transformDatePoint(date: Date | "") {
    // console.log ("transformDatePoint",date)
    if (!date) return "";
    const month =
      date.getMonth() + 1 < 10
        ? "0" + (date.getMonth() + 1)
        : date.getMonth() + 1;
    const day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    return `${day}.${month}.${date.getFullYear()}`;
  }

  transformStringPoint(date: string) {
    const dd = +date.substr(0, 2);
    const mm = +date.substr(3, 2);
    const yyyy = +date.substr(6, 4);
    return new Date(yyyy, mm - 1, dd);
  }

  public isVideo(url: string): boolean {
    return this.getMediaKindByExtension(url) === "video";
  }

  public isAudio(url: string): boolean {
    return this.getMediaKindByExtension(url) === "audio";
  }

  isAllowedFileExtension(url: string, allowedExtensions: string[]): boolean {
    return allowedExtensions.some((ext) => {
      return url.indexOf(ext) !== -1;
    });
  }

  isAllowedFileExtensionImg(
    url: string,
    allowedExtensions: string[] = this.allowedImg
  ): boolean {
    return allowedExtensions.some((ext) => {
      return url.indexOf(ext) !== -1;
    });
  }

  isAllowedFileExtensionAudio(
    url: string,
    allowedExtensions: string[] = this.allowedAudio
  ): boolean {
    return allowedExtensions.some((ext) => {
      return url.indexOf(ext) !== -1;
    });
  }

  isAllowedFileExtensionVideo(
    url: string,
    allowedExtensions: string[] = this.allowedVideo
  ): boolean {
    return allowedExtensions.some((ext) => {
      return url.indexOf(ext) !== -1;
    });
  }

  getExtensionFromFileName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) return "";
    return fileName.slice(lastDotIndex + 1).toLowerCase();
  }

  getErrorExtensionImg(allowedImg: string[] = this.allowedImg): string {
    return `Разрешены только изображения с расширениями: ${allowedImg.join(
      ", "
    )}`;
  }

  getErrorExtensionAudio(allowedAudio: string[] = this.allowedAudio): string {
    return `Разрешены только аудио с расширениями: ${allowedAudio.join(", ")}`;
  }

  getErrorExtensionVideo(allowedVideo: string[] = this.allowedVideo): string {
    return `Разрешены только видео с расширениями: ${allowedVideo.join(", ")}`;
  }

  transformHtmlString(html: string): string {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // remove script/style entirely
    doc.querySelectorAll("script, style").forEach((el) => el.remove());

    // recursive serializer that keeps only <b> and <i> (and maps strong->b, em->i),
    // converts lists to "item1; item2", and removes other tags while preserving their text.
    function nodeToString(node: Node): string {
      if (!node) return "";

      if (node.nodeType === Node.TEXT_NODE) {
        // normalize whitespace for text nodes
        return (node.textContent || "").replace(/\s+/g, " ").trim();
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return "";

      const el = node as Element;
      const tag = el.tagName.toLowerCase();

      if (tag === "script" || tag === "style") return "";

      // handle lists: join li children with '; '
      if (tag === "ul" || tag === "ol") {
        const items = Array.from(el.children)
          .filter((c) => c.tagName && c.tagName.toLowerCase() === "li")
          .map((li) => {
            // process li children and keep b/i tags inside
            const parts = Array.from(li.childNodes)
              .map(nodeToString)
              .filter(Boolean);
            return parts.join(" ").trim();
          })
          .filter(Boolean);
        return items.join("; ");
      }

      // map strong->b, em->i
      if (tag === "strong") {
        const inner = Array.from(el.childNodes)
          .map(nodeToString)
          .filter(Boolean)
          .join(" ");
        return inner ? `<b>${inner}</b>` : "";
      }
      if (tag === "em") {
        const inner = Array.from(el.childNodes)
          .map(nodeToString)
          .filter(Boolean)
          .join(" ");
        return inner ? `<i>${inner}</i>` : "";
      }

      // preserve b and i tags but strip attributes and process children
      if (tag === "b" || tag === "i") {
        const inner = Array.from(el.childNodes)
          .map(nodeToString)
          .filter(Boolean)
          .join(" ");
        return inner ? `<${tag}>${inner}</${tag}>` : "";
      }

      // other elements: return concatenated children separated by space
      const children = Array.from(el.childNodes)
        .map(nodeToString)
        .filter(Boolean);
      return children.join(" ");
    }

    // Process body children to avoid adding an extra wrapper
    const parts = Array.from(doc.body.childNodes)
      .map(nodeToString)
      .filter(Boolean);
    let result = parts.join(" ").replace(/\s+/g, " ").trim();

    // remove all quotation marks (optional per previous requests)
    result = result.replace(/["'«»]+/g, "").trim();

    return result;
  }

  /** Извлекает расширение в нижнем регистре без точки. */
  private extractExtension(input: string): string | undefined {
    // Удаляем query/hash, если пришёл URL
    const clean = input.split("#")[0].split("?")[0];
    const lastDot = clean.lastIndexOf(".");

    // Если передали просто ".png" или "png"
    if (lastDot === -1) {
      return this.trimDot(input).toLowerCase() || undefined;
    }

    const ext = clean.slice(lastDot + 1);
    return this.trimDot(ext).toLowerCase() || undefined;
  }

  private trimDot(s: string): string {
    return s.startsWith(".") ? s.slice(1) : s;
  }
}
